import { inject, Injectable, InjectionToken } from '@angular/core';
import { get, ref, remove } from 'firebase/database';
import { HttpsCallable, httpsCallable } from 'firebase/functions';
import { environment } from '../../environments/environment';
import { FIREBASE_DB, FIREBASE_FUNCTIONS } from './firebase';

/**
 * Where the API answers for this build. There is one Firebase project with two
 * databases behind it, and a laptop has no API of its own, so anything that is
 * not corti.to itself is pointed at dev's.
 */
export const API_BASE = `https://${
  environment.baseUrl.startsWith('corti.to') ? 'corti.to' : 'dev.corti.to'
}/api/v1`;

/** How many calls a token may make in an hour, as `api-tokens.ts` decides it. */
export const CALLS_PER_HOUR = 300;

/** How many tokens one person may hold, as `api-tokens.ts` decides it. */
export const MAX_TOKENS = 10;

/** One token as its owner sees it. The token itself is not in here to be seen. */
export interface ApiToken {
  id: string;
  name: string;
  prefix: string;
  createdAt: number;
  lastUsedAt?: number;
  calls?: number;
}

/** Where somebody's tokens live: the one node of theirs the app ever touches. */
export const tokensPath = (userId: string) => `users/${userId}/tokens`;

type Stored = Record<string, Omit<ApiToken, 'id'>>;

/**
 * What is kept under there, as the list the dialog draws: each one carrying the
 * key it was filed under, newest first.
 */
export function readTokens(stored: Stored | null | undefined): ApiToken[] {
  return Object.entries(stored ?? {})
    .map(([id, token]) => ({ id, ...token }))
    .sort((one, other) => other.createdAt - one.createdAt);
}

/** What making one answers, and the only time the token is ever readable. */
export interface NewApiToken {
  token: string;
  tokenId: string;
  name: string;
  createdAt: number;
}

/** The call is a token, so a test can answer for the function. */
export const CREATE_API_TOKEN = new InjectionToken<HttpsCallable<{ name: string }, NewApiToken>>(
  'CREATE_API_TOKEN',
  {
    providedIn: 'root',
    factory: () => httpsCallable(inject(FIREBASE_FUNCTIONS), 'createApiToken'),
  },
);

/**
 * The tokens the API is used with.
 *
 * Making one has to go through a function: what is stored is the token's hash,
 * under a node the rules close to everybody. Reading and revoking do not — the
 * entry under `users/<uid>/tokens` is the owner's, and deleting it is what
 * revoking means, because the API refuses a token whose entry is gone.
 */
@Injectable({ providedIn: 'root' })
export class ApiTokenService {
  private readonly db = inject(FIREBASE_DB);
  private readonly ask = inject(CREATE_API_TOKEN);

  async listFor(userId: string): Promise<ApiToken[]> {
    const snapshot = await get(ref(this.db, tokensPath(userId)));
    return readTokens(snapshot.val() as Stored | null);
  }

  async create(name: string): Promise<NewApiToken> {
    return (await this.ask({ name })).data;
  }

  async revoke(userId: string, tokenId: string): Promise<void> {
    await remove(ref(this.db, `${tokensPath(userId)}/${tokenId}`));
  }
}
